"use client";

import {
  AlertCircle,
  CheckCircle,
  CreditCard,
  Loader2,
  RefreshCw,
  X,
  XCircle,
} from "lucide-react";
import type { ActorRefFrom } from "xstate";
import type { paymentTestMachine } from "./stripe-payment-test.machine";

type PaymentMachineState = ReturnType<
  ActorRefFrom<typeof paymentTestMachine>["getSnapshot"]
>;

interface PaymentTestSectionProps {
  stripeReaderId: string | undefined | null;
  orgId: number | undefined;
  paymentState: PaymentMachineState; // XState State object from useMachine
  testAmount: string;
  onAmountChange: (amount: string) => void;
  onStartTest: () => void;
  onRetry: () => void;
  onReset: () => void;
  onCancel: () => void;
}

export function PaymentTestSection({
  stripeReaderId,
  orgId,
  paymentState,
  testAmount,
  onAmountChange,
  onStartTest,
  onRetry,
  onReset,
  onCancel,
}: PaymentTestSectionProps) {
  const isIdle = paymentState.matches("idle");
  const isCreatingIntent = paymentState.matches("creatingPaymentIntent");
  const isProcessing = paymentState.matches("processingPayment");
  const isCanceling = paymentState.matches("canceling");
  const isCanceled = paymentState.matches("canceled");
  const isSuccess = paymentState.matches("success");
  const isFailure = paymentState.matches("failure");

  const canStart = stripeReaderId && orgId && isIdle;
  const isActive = isCreatingIntent || isProcessing || isCanceling;

  return (
    <div className="space-y-3 pt-6 border-t border-gray-200">
      <div>
        <h4 className="text-sm font-medium text-gray-700">Test Payment</h4>
        <p className="text-xs text-gray-500 mt-1">
          Process a test payment using the processPOSPayment actor
        </p>
      </div>

      {/* Prerequisites Check */}
      {(!stripeReaderId || !orgId) && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-800">
              {!stripeReaderId && <div>• Please select a reader above</div>}
              {!orgId && <div>• Please configure Org ID in Settings tab</div>}
            </div>
          </div>
        </div>
      )}

      {/* Payment Form */}
      {!isSuccess && !isFailure && !isCanceled && (
        <div
          className={`bg-gray-50 border border-gray-200 rounded-lg p-4 ${
            !canStart ? "opacity-50" : ""
          }`}
        >
          <div className="space-y-3">
            <div>
              <label
                htmlFor="test-amount"
                className="text-xs font-medium text-gray-600"
              >
                Test Amount (USD)
              </label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-gray-700">$</span>
                <input
                  id="test-amount"
                  type="number"
                  step="0.01"
                  min="0.50"
                  disabled={!canStart || isActive}
                  value={testAmount}
                  onChange={(e) => onAmountChange(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum: $0.50</p>
            </div>

            <button
              type="button"
              onClick={onStartTest}
              disabled={!canStart || isActive}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              {isActive ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {isCreatingIntent && "Creating Payment Intent..."}
                  {isProcessing && "Processing on Terminal..."}
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4" />
                  Process Test Payment
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Status: Creating Payment Intent */}
      {isCreatingIntent && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Creating Payment Intent
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Generating payment intent via API...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Status: Processing Payment */}
      {isProcessing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">
                Processing Payment
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Polling terminal reader for payment status...
              </p>
            </div>
          </div>
          {paymentState.context.paymentIntentId && (
            <div className="text-xs font-mono text-blue-800 bg-blue-100 p-2 rounded">
              Payment Intent: {paymentState.context.paymentIntentId}
            </div>
          )}
          <button
            type="button"
            onClick={onCancel}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
          >
            <X className="h-4 w-4" />
            Cancel Payment
          </button>
        </div>
      )}

      {/* Status: Canceling Payment */}
      {isCanceling && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-amber-600 animate-spin" />
            <div>
              <p className="text-sm font-medium text-amber-900">
                Canceling Payment
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Sending cancellation request to terminal...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Canceled State */}
      {isCanceled && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-3">
            <XCircle className="h-6 w-6 text-gray-600" />
            <div>
              <p className="text-sm font-medium text-gray-900">
                Payment Canceled
              </p>
              <p className="text-xs text-gray-700 mt-1">
                The payment was successfully canceled
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onReset}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Start New Test
          </button>
        </div>
      )}

      {/* Success State */}
      {isSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-6 w-6 text-green-600" />
            <div>
              <p className="text-sm font-medium text-green-900">
                Payment Succeeded!
              </p>
              <p className="text-xs text-green-700 mt-1">
                The terminal has successfully processed the payment
              </p>
            </div>
          </div>
          {paymentState.context.paymentIntentId && (
            <div className="text-xs font-mono text-green-800 bg-green-100 p-2 rounded">
              Payment Intent: {paymentState.context.paymentIntentId}
            </div>
          )}
          <button
            type="button"
            onClick={onReset}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Start New Test
          </button>
        </div>
      )}

      {/* Failure State */}
      {isFailure && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
          <div className="flex items-start gap-3">
            <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Payment Failed</p>
              {paymentState.context.error && (
                <>
                  <p className="text-xs text-red-700 mt-1">
                    {paymentState.context.error.message}
                  </p>
                  <div className="mt-2 text-xs font-mono text-red-800 bg-red-100 p-2 rounded">
                    Error Code: {paymentState.context.error.code}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {paymentState.context.error?.retryable && (
              <button
                type="button"
                onClick={onRetry}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </button>
            )}
            <button
              type="button"
              onClick={onReset}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
